import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateProductInput,
  Product,
  ProductList,
  UpdateProductInput,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import { billingKeys } from '../billing/use-billing.js';
import {
  createProduct,
  deleteProduct,
  getProduct,
  getProducts,
  updateProduct,
  type ProductListParams,
} from './products-api.js';

/**
 * The catalogue's data layer.
 *
 * Every mutation invalidates the WHOLE product tree rather than one list, because the filters are
 * what a write moves a row between: retiring a product takes it out of the default list and into
 * the "show retired" one, and re-categorising it moves it between two chips. Patching a single
 * cached list would leave whichever list the row left still showing it.
 *
 * Nothing here is optimistic. A name can be refused by the server as a duplicate, and a screen
 * that showed the row first and took it away a moment later would be teaching a shop to distrust
 * what it just typed.
 */

export const productKeys = {
  all: ['products'] as const,
  list: (params: ProductListParams) => ['products', 'list', params] as const,
  detail: (productId: string) => ['products', 'detail', productId] as const,
};

export function useProducts(
  params: ProductListParams = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<ProductList> {
  const client = useApiClient();
  return useQuery({
    queryKey: productKeys.list(params),
    queryFn: () => getProducts(client, params),
    enabled: options.enabled ?? true,
  });
}

export function useProduct(
  productId: string,
  options: { enabled?: boolean } = {},
): UseQueryResult<Product> {
  const client = useApiClient();
  return useQuery({
    queryKey: productKeys.detail(productId),
    queryFn: () => getProduct(client, productId),
    enabled: (options.enabled ?? true) && productId.length > 0,
  });
}

export function useCreateProduct(): UseMutationResult<Product, unknown, CreateProductInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProductInput) => createProduct(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.all });
      // The allowance moved. Without this the in-module meter would keep reporting the figure
      // from before the thing that was just created, which is the one moment it has to be right.
      void queryClient.invalidateQueries({ queryKey: billingKeys.usage() });
    },
  });
}

export function useUpdateProduct(): UseMutationResult<
  Product,
  unknown,
  { productId: string; input: UpdateProductInput }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, input }: { productId: string; input: UpdateProductInput }) =>
      updateProduct(client, productId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

export function useDeleteProduct(): UseMutationResult<void, unknown, string> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) => deleteProduct(client, productId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}
